import React, { useRef, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import Styled from './styled';
import {
    Button,
    SendIcon,
    LoadingIndicator,
} from '../index';
import { useSessionContext } from '../../utils/SessionContext';
import { useTipTapEditor } from '../../utils/TipTap';
import { translated } from '../../utils/TranslationUtil';

const MAX_CHARACTER_LIMIT = 900;

export type FooterProps = {
    // accessibility support (alt text/aria-label, defaults to 'send')
    ariaLabel?: string;
    /** Optional Boolean to indicate when the Chat has ended */
    chatEnded?: boolean;
    /** Optional Boolean to disable TextEditor within Footer */
    disabled?: boolean;
    /** end chat handler */
    endChat?: () => void;
    /** Boolean field indicating the loading state */
    loading: boolean;
    /** On submit handler for Input Field  */
    onSubmit: (text: string) => Promise<void>;
    /** Optional Placeholder message for Footer Input Field */
    placeholder?: string;
    /** Optional boolean field indicating the reasoning state */
    reasoning?: boolean;
    /** Optional boolean field (defaults to false) to toggle rich vs plain text */
    richText?: boolean;
    /** Optional typing handler */
    onTyping?: () => void;
    /** Optional window type to determine the width of the widget */
    windowType?: 'mobile' | 'tablet' | 'desktop';
};

/**
 * Footer component that displays an optional title/subtitle.
 * 
 * @param {FooterProps} props - The properties to configure the Footer component.
 * @returns {JSX.Element} The rendered Footer component.
 */
export const Footer: React.FC<FooterProps> = ({
    ariaLabel = 'send',
    placeholder,
    onSubmit,
    loading,
    disabled = false,
    chatEnded = false,
    endChat,
    onTyping,
    reasoning = false,
    windowType = 'desktop',
}) => {
    const [hasText, setHasText] = useState(false);
    const sendButtonRef = useRef<HTMLDivElement>(null);
    const { enableBot } = useSessionContext();
    // Keep editor disabled during loading / disabled state
    const isBusy = loading || disabled;

    const {
        editor,
        submit,
        clear,
        characterCount,
        focus,
    } = useTipTapEditor({
        placeholder,
        submitter: async (html) => {
            if (isOverLimit) return;
            clear();
            await onSubmit(html);
            focus();
        },
        onUpdate: ({ editor }) => {
            setHasText(editor.getText().trim().length > 0);
        },
        sendMessage: onSubmit,
        ariaLabel: placeholder,
        editable: !isBusy,
    });

    const isOverLimit = characterCount > MAX_CHARACTER_LIMIT;

    return (
        <Styled.Footer disabled={chatEnded} reasoning={reasoning}>
            {chatEnded ?
                <Styled.ChatEnded>
                    <Button
                        label='Leave Chat'
                        onClick={endChat}
                        value='leave chat'
                    />
                </Styled.ChatEnded> :
                <Styled.Input windowType={windowType}>
                    <div
                        tabIndex={0}
                        onFocus={() => editor?.commands.focus()}
                    >
                        <EditorContent
                            aria-label={placeholder}
                            editor={editor}
                            role='textbox'
                            onKeyDown={(event) => {
                                if (event.key === 'Tab') {
                                    event.preventDefault();
                                    // Move focus to the Send button
                                    sendButtonRef.current?.focus();
                                } else if (event.key !== 'Enter') {
                                    onTyping && onTyping();
                                }
                            }}
                        />
                    </div>
                    {loading ?
                        <Styled.Loader>
                            <LoadingIndicator width='24px' height='24px' />
                        </Styled.Loader> :
                        <Styled.Send
                            ref={sendButtonRef}
                            onClick={submit}
                            disabled={isBusy || !hasText || isOverLimit}
                            aria-label={ariaLabel}
                            tabIndex={0}
                            role='button'
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault(); // Prevent default scrolling for Space key
                                    submit();
                                }
                            }}
                        >
                            <SendIcon />
                        </Styled.Send>
                    }
                    {isOverLimit &&
                        <Styled.OverLimitDisplay>
                            -{Math.max(0, characterCount - MAX_CHARACTER_LIMIT)}
                        </Styled.OverLimitDisplay>
                    }
                </Styled.Input>
            }
            {enableBot && <Styled.Subtext>{translated('Components.Footer.botText')}</Styled.Subtext>}
        </Styled.Footer>
    );
};
